import { Capacitor } from '@capacitor/core';
import { motion } from 'framer-motion';
import { 
  Lock, 
  Eye, 
  EyeOff, 
  CheckCircle, 
  AlertTriangle, 
  Shield, 
  Key, 
  ArrowRight,
  Zap,
  Home
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

import { resolveB2cFrontendUrl } from '../shared/seo/siteOrigin';
import LoadingSpinner from './LoadingSpinner';
import { supabase } from '../lib/supabase';
import { logEvent } from '../services/loggingService';
import { extractSessionTokensFromUrlHash } from '../utils/authUtils';
import { calculatePasswordStrength  } from '../utils/passwordValidation';
import type {PasswordStrength} from '../utils/passwordValidation';

import Footer from './Footer/Footer';
import Navigation from './Navigation/Navigation';

interface ResetPasswordPageProps {
  onNavigate?: (view: 'home' | 'login' | 'signup' | 'forgot-password' | 'reset-password' | 'privacy-policy' | 'cookie-policy' | 'terms-of-service' | 'gdpr-compliance' | 'payment-success') => void;
}

const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({ onNavigate }) => {
  const isNative = Capacitor.isNativePlatform();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [validSession, setValidSession] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [isFallbackMode, setIsFallbackMode] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    level: '',
    color: '',
    bgColor: '',
    suggestions: []
  });

  // Check for valid reset session on mount
  useEffect(() => {
    let isMounted = true;
    
    const checkResetSession = async () => {
      try {
        console.log('=��� RESET: Starting session check');
        console.log('=��� RESET: Current URL:', window.location.href);
        
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const tokenHash = urlParams.get('token_hash') || hashParams.get('token_hash');
        const type = urlParams.get('type');
        const mandatoryReset = sessionStorage.getItem('mandatory_password_reset');
        const storedToken = sessionStorage.getItem('recovery_token_hash');
        
        console.log('=��� RESET: Checking session with:', {
          hasTokenHash: !!tokenHash,
          type,
          hasMandatoryFlag: mandatoryReset === 'true',
          hasStoredToken: !!storedToken
        });
        
        // Method 1: Check for mandatory reset flag (WebContainer environment)
        if (mandatoryReset === 'true' || storedToken) {
          // Get stored email if available
          const targetEmail = sessionStorage.getItem('reset_target_email') || 'unknown@example.com';
          
          console.log('=��� RESET: Checking existing session for mandatory reset');
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session) {
            console.log('=��� RESET: Valid session found for mandatory reset');
            if (isMounted) {
              setValidSession(true);
              setCheckingSession(false);
            }
            return;
          } else {
            console.log('=��� RESET: No session found, but mandatory reset flag is set - enabling fallback mode');
            if (isMounted) {
              setIsFallbackMode(true);
              setValidSession(true);
              setCheckingSession(false);
            }
            return;
          }
        }
        
        // Method 2: Check if we have a token and can establish session
        if (tokenHash || storedToken) {
          console.log('=��� RESET: Found token, checking session...');
          
          // Check if session already exists
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session && isMounted) {
            console.log('=��� RESET: Valid session found');
            setValidSession(true);
            setCheckingSession(false);
            return;
          }
          
          // If no session but we have a token, enable fallback mode
          console.log('=��� RESET: No session found, enabling fallback mode');
          
          if (isMounted) {
            setIsFallbackMode(true);
            setValidSession(true);
            setCheckingSession(false);
          }
          return;
        }
        
        // If we get here, no valid reset context found
        throw new Error('No valid reset session found. Please use a fresh reset link from your email.');
        
      } catch (error) {
        console.error('Error checking reset session:', error);
        if (isMounted) {
          setValidSession(false);
          setError(error instanceof Error ? error.message : 'Unable to verify reset session');
          setCheckingSession(false);
        }
      }
    };
    
    checkResetSession();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Fallback token exchange effect - try to establish session if in fallback mode
  useEffect(() => {
    if (!isFallbackMode || !validSession) return;
    
    const attemptFallbackTokenExchange = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const tokenHash = urlParams.get('token') || urlParams.get('token_hash');
        const type = urlParams.get('type');
        
        if (tokenHash && type === 'recovery') {
          console.log('=��� FALLBACK: Attempting token exchange for fallback mode');
          
          // Try to exchange the token for a session
          const { data, error } = await supabase.auth.exchangeCodeForSession(
            `${resolveB2cFrontendUrl()}/auth/confirm?token_hash=${tokenHash}&type=recovery`
          );
          
          if (!error && data.session) {
            console.log('=��� FALLBACK: Token exchange successful');
          } else {
            console.warn('=��� FALLBACK: Token exchange failed, will use manual validation:', error?.message);
          }
        }
      } catch (error) {
        console.warn('=��� FALLBACK: Token exchange attempt failed:', error);
        // Continue with manual validation
      }
    };
    
    attemptFallbackTokenExchange();
  }, [isFallbackMode, validSession]);

  // Calculate password strength when password changes
  useEffect(() => {
    if (newPassword) {
      setPasswordStrength(calculatePasswordStrength(newPassword));
    } else {
      setPasswordStrength({
        score: 0,
        level: '',
        color: '',
        bgColor: '',
        suggestions: []
      });
    }
  }, [newPassword]);

  const checkResetSession = async () => {
    try {
      setCheckingSession(true);
      
      // Get URL parameters and hash
      const urlParams = new URLSearchParams(window.location.search);
      const type = urlParams.get('type');
      const tokenHash = urlParams.get('token_hash');
      const accessToken = urlParams.get('access_token');
      const refreshToken = urlParams.get('refresh_token');
      
      console.log('SECURITY: Processing auth confirmation', {
        pathname: window.location.pathname,
        type,
        hasTokenHash: !!tokenHash,
        hasAccessToken: !!accessToken,
        fullURL: window.location.href
      });
      
      // CRITICAL: Set flags immediately to prevent redirect loops  
      if (type === 'recovery' && tokenHash) {
        console.log('SECURITY: Setting security flags to prevent redirects');
        sessionStorage.setItem('mandatory_password_reset', 'true');
        sessionStorage.setItem('password_reset_in_progress', 'true');
        sessionStorage.setItem('recovery_session_id', tokenHash);
        
        // For recovery flow, check if we already have a session or need to get one
        console.log('SECURITY: Checking for existing session...');
        let { data: sessionData, error } = await supabase.auth.getSession();
        
        // If no session exists, try to get one using the URL hash
        if (!sessionData.session && window.location.hash) {
          console.log('SECURITY: No session found, checking URL hash...');
          // Let Supabase handle the hash automatically
          await new Promise(resolve => setTimeout(resolve, 1000)); // Wait for hash processing
          const { data: newSessionData } = await supabase.auth.getSession();
          sessionData = newSessionData;
        }
        
        // If still no session, try manual token extraction from hash
        if (!sessionData.session && window.location.hash) {
          console.log('SECURITY: Attempting manual token extraction...');
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const hashAccessToken = hashParams.get('access_token');
          const hashRefreshToken = hashParams.get('refresh_token');
          
          if (hashAccessToken && hashRefreshToken) {
            const { data: setSessionData, error: setSessionError } = await supabase.auth.setSession({
              access_token: hashAccessToken,
              refresh_token: hashRefreshToken
            });
            
            if (!setSessionError && setSessionData.session) {
              sessionData = setSessionData;
            }
          }
        }
        
        if (sessionData.session) {
          console.log('SECURITY: Recovery session established - user MUST reset password');
          setValidSession(true);
          return;
        } else {
          console.error('SECURITY: No session could be established');
          throw new Error('Unable to establish recovery session. Please try clicking the reset link again.');
        }
      }
      
      // Clear flags if no recovery tokens found
      sessionStorage.removeItem('mandatory_password_reset');
      sessionStorage.removeItem('password_reset_in_progress');
      sessionStorage.removeItem('recovery_session_id');
      // If we get here without a valid session, show error
      throw new Error('Invalid or expired reset link. Please request a new password reset.');
      
    } catch (err) {
      console.error('Error checking reset session:', err);
      // Clear flags on any error
      sessionStorage.removeItem('mandatory_password_reset');
      sessionStorage.removeItem('password_reset_in_progress');
      sessionStorage.removeItem('recovery_session_id');
      setValidSession(false);
      setError(err instanceof Error ? err.message : 'Unable to verify reset session. Please try again.');
    } finally {
      setCheckingSession(false);
    }
  };

  const validateForm = () => {
    if (!newPassword) {
      setError('New password is required');
      return false;
    }
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }
    
    if (passwordStrength.score < 3) {
      setError('Please choose a stronger password');
      return false;
    }
    
    if (!confirmPassword) {
      setError('Please confirm your password');
      return false;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError(null);

    try {
      if (isFallbackMode) {
        // FALLBACK MODE: Manual token validation and password update
        console.log('=��� FALLBACK: Processing password update in fallback mode');
        
        // Get token from sessionStorage (set by AuthConfirmPage)
        const tokenHash = sessionStorage.getItem('recovery_token_hash');
        
        if (!tokenHash) {
          console.error('=��� FALLBACK: No token found in sessionStorage');
          throw new Error('No reset token found. Please click the reset link from your email again.');
        }
        
        console.log('=��� FALLBACK: Using stored token for manual reset');
        
        // Try to get current session first
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (session) {
          // We have a session, try to update password
          console.log('=��� FALLBACK: Using existing session for password update');
          const { error: updateError } = await supabase.auth.updateUser({
            password: newPassword
          });
          
          if (updateError) {
            throw new Error(updateError.message);
          }
        } else {
          // No session - use Edge Function for manual password reset
          console.log('=��� FALLBACK: No session, using Edge Function for manual reset');
          
          const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manual-password-reset`;
          
          console.log('=��� FALLBACK: Calling Edge Function with token');
          
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              token: tokenHash,
              newPassword: newPassword,
              email: session?.user?.email // Include email as fallback identifier
            })
          });
          
          console.log('=��� FALLBACK: Edge Function response status:', response.status);
          
          if (!response.ok) {
            const responseText = await response.text();
            console.error('=��� FALLBACK: Edge Function error response:', responseText);
            
            let errorData;
            try {
              errorData = JSON.parse(responseText);
            } catch {
              errorData = { error: 'Failed to reset password' };
            }
            
            throw new Error(errorData.error || 'Failed to reset password');
          }
          
          const responseData = await response.json();
          console.log('=��� FALLBACK: Edge Function success:', responseData);
          console.log('=��� FALLBACK: Password reset via Edge Function successful');
        }
      } else {
        // NORMAL MODE: Standard Supabase password update
        console.log('=��� NORMAL: Processing password update in normal mode');
        
        // Double-check we have a valid recovery session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          throw new Error(`Session error: ${sessionError.message}`);
        }
        
        if (!session || !session.user) {
          throw new Error('Auth session missing! Please use a fresh reset link from your email.');
        }
        
        // Update password using Supabase
        const { error: updateError } = await supabase.auth.updateUser({
          password: newPassword
        });
        
        if (updateError) {
          throw new Error(updateError.message);
        }
      }

      console.log('SECURITY: Password updated successfully');
      
      // SECURITY: Clear mandatory password reset flag
      sessionStorage.removeItem('mandatory_password_reset');
      sessionStorage.removeItem('recovery_session_id');
      sessionStorage.removeItem('password_reset_in_progress');
      sessionStorage.removeItem('reset_token_hash');
      
      // Send password change confirmation email
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-password-changed-email`;
        
        await fetch(apiUrl, {
          method: 'POST',
          headers: {
            ...(session && { 'Authorization': `Bearer ${session.access_token}` }),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: session?.user.email || 'user@example.com',
            name: session?.user.user_metadata?.name || 'User'
          })
        });
      } catch (emailError) {
        console.warn('Failed to send password change confirmation email:', emailError);
        // Don't fail the password reset if email fails
      }

      setSuccess(true);

      await logEvent({
        level: 'info',
        action: 'password_reset_completed',
        details: { 
          passwordStrengthScore: passwordStrength.score,
          method: 'reset_link'
        }
      });

      // CRITICAL SECURITY: Force sign out and redirect to login after 3 seconds
      window.setTimeout(async () => {
        try {
          console.log('SECURITY: Forcing logout after password reset');
          
          // Mark that password reset is complete
          sessionStorage.setItem('password_reset_complete', 'true');
          
          // Clear all session data first
          sessionStorage.clear();
          localStorage.clear();
          
          // Sign out from Supabase
          await supabase.auth.signOut();
          
          console.log('SECURITY: User signed out successfully');
          
          // Redirect to login with success message
          window.location.href = '/login?reset=success';
        } catch (signOutError) {
          console.error('Error signing out:', signOutError);
          // CRITICAL: Force redirect even if signout fails for security
          console.log('SECURITY: Forcing redirect despite signout error');
          window.location.href = '/login?reset=success';
        }
      }, 3000);

    } catch (err) {
      console.error('Password reset error:', err);
      setError(err instanceof Error ? err.message : 'Failed to reset password');
      
      await logEvent({
        level: 'error',
        action: 'password_reset_failed',
        details: { error: err instanceof Error ? err.message : String(err) },
        error: err instanceof Error ? err : new Error(String(err))
      });
    } finally {
      setLoading(false);
    }
  };

  // Loading state while checking session
  if (checkingSession) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" showText={false} />
          <p className="text-white text-lg">Verifying reset session...</p>
        </div>
      </div>
    );
  }

  // Invalid session state
  if (!validSession) {
    return (
      <div
        className="min-h-screen flex flex-col"
        style={isNative ? { minHeight: '100dvh', background: 'var(--vscode-bg-primary)' } : { background: '#111827' }}
      >
        <Helmet>
          <title>Invalid Reset Link - GoGrabJob</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>

        <Navigation onNavigate={onNavigate} />
        
        <div className="flex-1 flex items-center justify-center px-4 py-12 pt-24">
          <div className="max-w-md w-full text-center">
            <div className="bg-gray-800 rounded-2xl p-8 border border-red-500/30">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>
              
              <h1 className="text-2xl font-bold text-white mb-4">Invalid Reset Link</h1>
              <p className="text-red-300 mb-6">{error}</p>
              
              <div className="space-y-3">
                <button
                  onClick={() => onNavigate?.('forgot-password')}
                  className="w-full bg-[var(--vscode-primary)] hover:bg-[var(--vscode-primary-hover)] text-[var(--vscode-primary-text)] font-semibold py-3 px-4 rounded-lg transition-colors"
                >
                  Request New Reset Link
                </button>
                <button
                  onClick={() => onNavigate?.('login')}
                  className="w-full bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                >
                  Back to Login
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <Footer onNavigate={onNavigate} />
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={isNative ? { minHeight: '100dvh', background: 'var(--vscode-bg-primary)' } : { background: '#111827' }}
      >
        <div className="max-w-md w-full text-center px-4">
          <motion.div
            className="bg-gray-800 rounded-2xl p-8 border border-green-500/30"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div
              className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 300 }}
            >
              <CheckCircle className="w-10 h-10 text-green-400" />
            </motion.div>
            
            <h1 className="text-2xl font-bold text-white mb-4">Password Reset Successful!</h1>
            <p className="text-green-300 mb-6">
              Your password has been updated successfully. You will be signed out and redirected to the login page.
            </p>
            
            <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5 text-green-400" />
                <span className="text-green-300 text-sm">
                  A confirmation email has been sent to your email address.
                </span>
              </div>
            </div>
            
            <div className="text-center">
              <p className="text-gray-400 text-sm">
                Redirecting to login page in 3 seconds...
              </p>
              <div className="mt-4">
                <motion.div
                  className="w-full h-1 bg-gray-700 rounded-full overflow-hidden"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <motion.div
                    className="h-full bg-green-400"
                    initial={{ width: '0%' }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 3, ease: 'linear' }}
                  />
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Main reset password form
  return (
    <div
      className="min-h-screen flex flex-col"
      style={isNative ? { minHeight: '100dvh', background: 'var(--vscode-bg-primary)' } : { background: '#111827' }}
    >
      <Helmet>
        <title>Reset Password - GoGrabJob</title>
        <meta name="description" content="Reset your GoGrabJob account password securely." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Navigation onNavigate={onNavigate} />
      
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[var(--vscode-primary)]/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>
      </div>
      
      <div className="flex-1 flex items-center justify-center px-4 py-12 pt-24 relative z-10">
        <motion.div
          className="w-full max-w-md mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="bg-gray-800 rounded-2xl shadow-xl border border-gray-700 p-6 md:p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-purple-500 rounded-xl flex items-center justify-center shadow-lg">
                  <Lock className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold text-white">GoGrabJob</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Reset Your Password
              </h1>
              <p className="text-gray-400">
                Enter your new password below
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <motion.div
                className="mb-6 p-4 bg-red-900/50 border border-red-500/30 rounded-lg text-red-300"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center">
                  <AlertTriangle className="w-5 h-5 text-red-400 mr-2" />
                  <span>{error}</span>
                </div>
              </motion.div>
            )}

            {/* Reset Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* New Password Field */}
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-300 mb-2">
                  New Password *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Key className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setError(null);
                    }}
                    className="w-full pl-10 pr-12 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition-all duration-300"
                    placeholder="Enter your new password"
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-300 transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                
                {/* Password Strength Indicator */}
                {newPassword && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-gray-400">Password Strength</span>
                          {passwordStrength.level && (
                            <span className={`text-xs font-medium ${passwordStrength.color}`}>
                              {passwordStrength.level}
                            </span>
                          )}
                        </div>
                        <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                          <motion.div
                            className={`h-full transition-all duration-300 ${passwordStrength.bgColor}`}
                            initial={{ width: 0 }}
                            animate={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                            transition={{ duration: 0.3 }}
                          />
                        </div>
                      </div>
                      
                      {passwordStrength.score >= 4 && (
                        <Shield className="w-5 h-5 text-green-400" />
                      )}
                    </div>
                    
                    {passwordStrength.suggestions.length > 0 && (
                      <div className="bg-gray-700/50 p-3 rounded-lg border border-gray-600">
                        <p className="text-yellow-400 font-medium text-xs mb-1">Suggestions:</p>
                        <ul className="text-xs text-gray-300 space-y-1">
                          {passwordStrength.suggestions.map((suggestion, index) => (
                            <li key={index} className="flex items-center">
                              <span className="w-1 h-1 bg-gray-400 rounded-full mr-2"></span>
                              {suggestion}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                  Confirm New Password *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setError(null);
                    }}
                    className="w-full pl-10 pr-12 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent transition-all duration-300"
                    placeholder="Confirm your new password"
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-300 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !newPassword || !confirmPassword || passwordStrength.score < 3}
                className="w-full flex items-center justify-center space-x-2 py-3 px-4 bg-[var(--vscode-primary)] hover:bg-[var(--vscode-primary-hover)] text-[var(--vscode-primary-text)] font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-cyan-500/25"
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="sm" showText={false} />
                    <span>Updating Password...</span>
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    <span>Update Password</span>
                  </>
                )}
              </button>
            </form>

            {/* Security Notice */}
            <div className="mt-6 bg-cyan-900/20 border border-cyan-500/30 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Shield className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-cyan-400 font-medium text-sm">Security Notice</h4>
                  <p className="text-cyan-300 text-xs mt-1">
                    After updating your password, you'll be signed out for security and will need to sign in again with your new password.
                  </p>
                </div>
              </div>
            </div>

            {/* Password Requirements */}
            <div className="mt-6 bg-gray-700/50 p-4 rounded-lg border border-gray-600">
              <h4 className="text-white font-medium mb-3 text-sm">Password Requirements</h4>
              <ul className="space-y-1 text-xs text-gray-300">
                <li className="flex items-center">
                  <CheckCircle className={`w-3 h-3 mr-2 ${newPassword.length >= 6 ? 'text-green-400' : 'text-gray-500'}`} />
                  At least 6 characters long
                </li>
                <li className="flex items-center">
                  <CheckCircle className={`w-3 h-3 mr-2 ${/[A-Z]/.test(newPassword) ? 'text-green-400' : 'text-gray-500'}`} />
                  Contains uppercase letter
                </li>
                <li className="flex items-center">
                  <CheckCircle className={`w-3 h-3 mr-2 ${/[a-z]/.test(newPassword) ? 'text-green-400' : 'text-gray-500'}`} />
                  Contains lowercase letter
                </li>
                <li className="flex items-center">
                  <CheckCircle className={`w-3 h-3 mr-2 ${/\d/.test(newPassword) ? 'text-green-400' : 'text-gray-500'}`} />
                  Contains number
                </li>
                <li className="flex items-center">
                  <CheckCircle className={`w-3 h-3 mr-2 ${/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword) ? 'text-green-400' : 'text-gray-500'}`} />
                  Contains special character
                </li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
      
      <Footer onNavigate={onNavigate} />
    </div>
  );
};

export default ResetPasswordPage;
